import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Get Cash Receipt Step
 *
 * Loads a cash receipt and exposes its facts for routing. Exists so a
 * TASK_CREATED workflow on the log-cash-receipt task type — whose payload only
 * carries the receipt as the task's anchor entity — can decide whether the
 * receipt needs keying (e.g. a Switch on `originSystem`, then Complete Task
 * with an outcome), or whether to request its remittance from the
 * customer's portal (`lifecycleStatus` needs_remittance, `remittanceType`
 * portal). Values are raw: which sources count as "already booked" is
 * configured in the workflow, never in this step.
 */
export const getCashReceiptStep = defineStepConfig({
  type: "get_cash_receipt",
  name: "Get Cash Receipt",
  description:
    "Load a cash receipt by entity reference or id and expose its source, amount and customer",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    cashReceipt: entityOfType(SystemEntity.AR_CASH_RECEIPT)
      .optional()
      .describe(
        "The cash receipt entity reference (e.g. a task trigger's anchorEntity). Must reference a cash receipt.",
      ),
    cashReceiptId: z
      .uuid()
      .optional()
      .describe(
        "Bare cash receipt id — alternative to `cashReceipt`. Ignored when `cashReceipt` is wired.",
      ),
  }),

  outputSchema: z.object({
    cashReceipt: entityOfType(SystemEntity.AR_CASH_RECEIPT).describe(
      "The loaded cash receipt entity reference",
    ),
    cashReceiptId: z.uuid().describe("The receipt's id (flat, for wiring)"),
    status: z
      .string()
      .describe("The receipt's review status (needs_review, matched, …)"),
    originSystem: z
      .string()
      .nullable()
      .describe(
        "The system the receipt was ingested from (e.g. plaid, email, manual, or the ERP's own key)",
      ),
    originId: z
      .string()
      .nullable()
      .describe("The receipt's id in its source system, when it has one"),
    amount: z
      .string()
      .nullable()
      .describe("Receipt amount as a decimal string"),
    transactionDate: z
      .string()
      .nullable()
      .describe("Receipt date (YYYY-MM-DD)"),
    paymentChannel: z
      .string()
      .nullable()
      .describe("How the cash arrived (check, ach, wire, …), when known"),
    customerId: z
      .uuid()
      .nullable()
      .describe("The matched AR customer, when one is set"),
    remittanceType: z
      .string()
      .nullable()
      .describe(
        "How the receipt's customer sends remittance advice (email, portal, edi, mail, none); null when there is no customer or it is not set",
      ),
    merchantName: z
      .string()
      .nullable()
      .describe("The payer as the source described them"),
    referenceNumber: z
      .string()
      .nullable()
      .describe("Check number or bank reference, when the source gave one"),
    lifecycleStatus: z
      .string()
      .describe(
        "Where the receipt is in being filed, as the cash-receipts list shows it (needs_remittance, ready_for_entry, needs_review, keyed, closed, before_tracking)",
      ),
  }),

  configSchema: z.object({}),

  emits: [] as const,
});
